import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Input as MuiInput, Typography } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Dayjs } from "dayjs";

import { CustomTable } from "../../components/DataTable";
import { ReactComponent as SearchIcon } from "../../assets/svg/Search.svg";
import { ReactComponent as WarningIcon } from "../../assets/svg/WarningRed.svg";
import { RootState, useAppSelector } from "../../store";
import { colors } from "../../theme/colors";
import { CustomActionMenu } from "../../components/Menu";
import { useGetPathologyRegisterPatientSpecimenRecordsQuery } from "../../services/api";
import style from "./discrepantSpecimen.module.css";
import { ReactComponent as EyeIcon } from "../../assets/svg/eye.svg";
import { SingleSelectInput } from "../../components/SelectInput";
import { requestFormStatus } from "../../utils/constants";
import DateInput from "../../components/UserInput/DateInput";

// Functional component representing the list of discrepant specimen information
const DiscrepantSpecimens = () => {
  const navigate = useNavigate();
  const { userRole, userDetails } = useAppSelector((state: RootState) => state.auth);

  const [selectedFilter, setSelectedFilter] = useState("All");
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);

  const fields = [
    {
      id: "patient_surname",
      label: "Patient Name",
      flex: 1.5,
      type: "name",
      sortable: true,
      render: (item: PatientRecord) => (
        <Typography
          className={style.cell}
          sx={{
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: "2",
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {`${(item.patient_surname ?? "").toString().toUpperCase()}, ${item.patient_given_name ?? ""}`}
        </Typography>
      ),
    },
    {
      id: "laboratory",
      label: "Laboratory",
      flex: 1.5,
      type: "name",
      sortable: true,
      render: (item: PatientRecord) => (
        <Typography
          className={style.cell}
          sx={{
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: "2",
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {`${item["laboratory"] ?? ""}`}
        </Typography>
      ),
    },
    {
      id: "surgeon_surname",
      label: "Surgeon",
      flex: 1.5,
      type: "name",
      sortable: true,
      render: (item: PatientRecord) => (
        <Typography
          className={style.cell}
          sx={{
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: "2",
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {`${(item.surgeon_surname ?? "").toString().toUpperCase()}, ${item.surgeon_given_name ?? ""} ${item.surgeon_prefix ?? ""}`}
        </Typography>
      ),
    },
    {
      id: "mrn_number",
      label: "MRN",
      flex: 1,
      type: "name",
      sortable: true,
      render: (item: PatientRecord) => (
        <Typography
          className={style.cell}
          sx={{
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: "2",
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {`${item.mrn_number ?? ""}`}
        </Typography>
      ),
    },
    {
      id: "status",
      label: "Status",
      type: "badge",
      flex: 1.8,
      sortable: true,
      render: (item: PatientRecord) =>
        item?.status ? (
          <Box
            className={style.statusContainer}
            sx={{
              backgroundColor:
                item?.status === "READY FOR PICKUP"
                  ? colors.lightGreen
                  : colors.modalBackground,
            }}
          >
            {item?.is_discrepancy && <WarningIcon width={16} height={16} />}
            <Typography className={style.cell} sx={{ fontSize: "12px" }}>
              {item?.status !== "None" ? item?.status : ""}
            </Typography>
          </Box>
        ) : (
          <></>
        ),
    },
    {
      id: "logo_s3_key_name",
      label: "Actions",
      type: "action",
      flex: 0.5,
      render: (item: PatientRecord) => (
        <CustomActionMenu
          item={item}
          isActive={item?.is_active ?? false}
          handleChange={handleMenu}
          actions={[
            {
              label: "View",
              value: "view",
              icon: <EyeIcon height={24} width={24} />,
            },
          ]}
        />
      ),
    },
  ];

  // all query parameters and pagination params
  const [queryParam, setQueryParam] = useState({
    search_key: "",
    is_discrepancy: true,
    limit: 10,
    offset: 0,
    is_request_form_printed: true,
    order_by_columns: "created_at",
    order: "Asc",
  });

  // for searching in the query
  const handleSearch = useCallback(
    (e: any) => {
      setQueryParam((prevParams) => ({
        ...prevParams,
        search_key: e.target.value === "" ? undefined : e.target.value,
        offset: 0, // Reset offset when performing a new search
      }));
    },
    [setQueryParam]
  );

  // for handling the specimen state
  const handleMenu = (value: string, item: PatientRecord) => {
    if (value === "view") {
      navigate(`/specimens/details/${item?.id}`, {
        state: { isDiscrepancyView: selectedFilter === "Discrepancy" },
      });
    }
  };

  const healthOrgId = (userDetails)?.health_org_id;

  const { data: patientRecords } = useGetPathologyRegisterPatientSpecimenRecordsQuery(
    { healthOrgId: healthOrgId as number, query: queryParam },
    { skip: !healthOrgId }
  );

  // handle the page size change
  const handleSizeChange = useCallback(
    (e: any) => {
      setQueryParam((prevParams) => ({
        ...prevParams,
        limit: e.target.value,
      }));
    },
    [setQueryParam]
  );

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: "30px"
        }}
      >
        <Box sx={{ width: 250 }}>
          <SingleSelectInput
            placeholder="Select status"
            label=""
            name="status"
            data={requestFormStatus ?? []}
            value={(queryParam as any).status ?? ""}
            onChange={(e) => setQueryParam((prev) => ({ ...prev, status: (e.target.value as string) || undefined }))}
            onClear={() => setQueryParam((prev) => { const { status, ...rest } = prev as any; return rest; })}
          />
        </Box>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateInput
            value={fromDate}
            onChange={setFromDate}
            label="From"
          />
          <DateInput
            value={toDate}
            onChange={setToDate}
            minDate={fromDate}
            label="To"
          />
        </LocalizationProvider>

        <MuiInput
          disableUnderline
          placeholder={
            (userRole === "OT_ADMIN")
              ? "Search by Patient/MRN/Laboratory/Surgeon"
              : userRole === "LABORATORY_ADMIN" ? "Search by Patient/MRN" : "Search by name...."
          }
          type="search"
          startAdornment={<SearchIcon width={20} height={20} />}
          value={queryParam.search_key}
          onChange={handleSearch}
          sx={{
            width: "320px",
            gap: "8px",
            borderRadius: "50px",
            marginTop: "20px",
            backgroundColor: colors.white
          }}
        />
      </Box>
      <Box style={{ maxHeight: "calc(100vh - 195px)", overflow: "auto" }}>
        <CustomTable<PatientRecord>
          fields={fields}
          paginationRequired={true}
          data={patientRecords || []}
          onDoubleClick={(patientRecord: PatientRecord) => {
            navigate(`/specimens/details/${patientRecord?.id}`, {
              state: { isDiscrepancyView: selectedFilter === "Discrepancy" },
            });
          }}
          defaultOrderBy={"created_at"}
          searchTerm={queryParam.search_key}
          onClearSearch={() => setQueryParam((prev) => ({ ...prev, search_key: "" }))}
        // defaultOrder={queryParam?.order === "dsc" ? "desc" : "asc"}
        // onOrderChange={() => {
        //   setQueryParam((prevParams) => ({
        //     ...prevParams,
        //     order: prevParams?.order === "asc" ? "dsc" : "asc",
        //   }));
        // }}
        // onOrderByChange={(column: string) => {
        //   setQueryParam((prevParams) => ({
        //     ...prevParams,
        //     order_by_columns: column,
        //   }));
        // }}
        />
      </Box>
    </Box>
  );
};
export default DiscrepantSpecimens;
